import { useEffect, useState } from 'react';

import { useHostingAccount } from '@newfold/huapi-js';
import { AccountHostingList200AccountsItem } from '@newfold/huapi-js/src/index.schemas';

import { useMFEContext } from '~/components/MFEProvider';
import { useTenant } from '~/components/TenantProvider';
import HostingLoading from '~/scenes/Hosting/scenes/HostingLoading';
import { DISABLE_STATUSES } from '~/utils/hosting/constants';
import { getHostingDisabled } from '~/utils/hosting/helpers';

import useAccount from './useAccount';

export const defaultOptions = {
  maxAttempts: 20,
  maxFailureAttempts: 60,
  retryDelay: 5000,
  refetchInterval: 5000,
  isProvisioning: false,
  enabled: true,
};

type HostingInfoTypes =
  | {
      hostingId?: string | number | null;
      key?: string;
      options?: {
        maxAttempts?: number;
        maxFailureAttempts?: number;
        retryDelay?: number;
        refetchInterval?: number;
        isProvisioning?: boolean;
        enabled?: boolean;
      };
      hostingList?: AccountHostingList200AccountsItem[];
    }
  | undefined;

const useHostingInfo = (
  props: HostingInfoTypes = {
    hostingId: undefined,
    key: undefined,
    options: defaultOptions,
    hostingList: undefined,
  },
) => {
  // Disabling lint rule for unused var `otherProps` as it's expected to be used in the future.
  // eslint-disable-next-line no-unused-vars
  const { hostingId, key, options, hostingList, ...otherProps } = props;
  const [pendingAttemptCount, setPendingAttemptCount] = useState(0);
  const [failureAttemptCount, setFailureAttemptCount] = useState(
    props?.options?.maxFailureAttempts!,
  );
  const suspendedStates = ['disable', 'disabled'];
  const decomStates = [
    'decom',
    'pending_decom',
    'failed_decom',
    'pending_stop_decom',
    'failed_stop_decom',
  ];
  const {
    // @ts-expect-error
    hostingStatus: {
      isProvisioning,
      // TODO: Can we get these statuses from the useHostingAccount hook?
      renewalStatus: { isExpired, willExpireSoon },
    },
  } = useMFEContext();
  const { isValidated: isTenantValidated } = useTenant();
  const { id: contextId } = useAccount();

  const id = hostingId || contextId;

  const {
    data: hostingAccount,
    error,
    failureCount,
    failureReason,
    isPending,
    isSuccess,
    ...restProps
  } = useHostingAccount(id, {
    query: {
      enabled: !!id && options?.enabled && isTenantValidated,
      // Propose we remove the `queryKey` OR update it to something that's unique as it's sometimes called using the hosting ID and somtimes using the SiteID
      ...(key && { queryKey: [key] }),

      /*
      if AMM provides a "provisioning" hosting status and we get 403 responses
        keep retrying util we get a 200 response or after 20 minutes show an error message
      if AMM provides an "active" hosting status and we get 403
        stop retrying and show an error message
      any type of other error status (400, 512, etc...) will stop retrying and show an error message
      */
      retry: (failureCount, error) => {
        const keepPolling = error?.response?.status === 403 && isProvisioning;
        keepPolling
          ? setFailureAttemptCount(failureCount)
          : setFailureAttemptCount(options?.maxFailureAttempts!);
        return keepPolling
          ? failureCount < options?.maxFailureAttempts!
          : false;
      },
      retryDelay: options?.retryDelay,
      refetchInterval: (query) => {
        const status = query?.state?.data?.data?.status!;
        return (
          ['pending_create', 'create'].includes(status) &&
          pendingAttemptCount < options?.maxAttempts! &&
          options?.refetchInterval!
        );
      },
    },
    ...otherProps,
  });

  // onSuccess - useHostingAccount
  useEffect(() => {
    if (
      isSuccess &&
      ['pending_create', 'create'].includes(hostingAccount?.data?.status!)
    ) {
      setPendingAttemptCount((old) => old + 1);
    }
  }, [isSuccess, hostingAccount]);

  const hostingStatus = hostingAccount?.data?.status!;

  const isDeleted = ['deleted'].includes(hostingStatus);

  const isDeleting = ['pending_destroy', 'destroy'].includes(hostingStatus);

  const isPendingCreate =
    ['pending_create', 'create'].includes(hostingStatus) &&
    pendingAttemptCount < options?.maxAttempts!;

  // Should break the isProvisioning check out from a separate (new) value from HUAPI/FG
  const isProvisioningExtended =
    failureReason?.response?.status === 403 &&
    isProvisioning &&
    failureAttemptCount < options?.maxFailureAttempts!;

  const isError =
    !!error?.response?.status ||
    pendingAttemptCount >= options?.maxAttempts! ||
    (hostingStatus?.indexOf('failed') > -1 &&
      !hostingStatus?.includes('failed_stop_decom'));

  const allHostingDisabled =
    hostingList?.filter(({ status }) => DISABLE_STATUSES.includes(status!))
      .length === hostingList?.length;

  return {
    ...restProps,
    hostingId: id,
    isPending,
    isAwaiting: isPendingCreate || isProvisioningExtended,
    // TODO: isDeleting here is temporary until we have messaging for destroy, pending_destroy and failed_destroy
    isDeleted: isDeleted || isDeleting,
    isDeleting,
    isError,
    isSuccess,
    isDisabled: isPending || getHostingDisabled(hostingStatus),
    isSuspended: isPending || suspendedStates.includes(hostingStatus),
    isDecommissioned: isPending || decomStates.includes(hostingStatus),
    // isValid - general indiciation that hosting is ready to use in any component
    isValid: !isError && !isPending && hostingAccount && !isPendingCreate,
    isExpired,
    willExpireSoon,
    hostingAccount,
    errorStatusCode: failureReason?.response?.status,
    failureCount,
    allHostingDisabled,
    HostingLoading: () => (
      <HostingLoading
        loading={isPending}
        pending={isPendingCreate || isProvisioningExtended}
        deleted={isDeleted || isDeleting}
        statusCode={failureReason?.response?.status}
        isError={!!isError}
      />
    ),
  };
};

export default useHostingInfo;
